import { useEffect, useState } from "react";
import { getStoredToken } from "../api/client";
import { leadCatBrand } from "../brand/brandConfig";
import { BrandLogo } from "../components/BrandLogo/BrandLogo";
import { LeadCatHeroArtwork } from "../components/LeadCatHeroArtwork/LeadCatHeroArtwork";
import { LeadSourceIcon } from "../components/LeadSourceBadge/LeadSourceBadge";
import "./MarketingHome.css";
import "./MarketingLanding.css";
import { MarketingFeatures } from "./MarketingFeatures";
import { MarketingDemoSection } from "./MarketingDemoSection";
import { MarketingPricingSection } from "./MarketingPricingSection";
import { MarketingStorefrontStory } from "./MarketingStorefrontStory";
import { fetchPublicCrmPricing, type PublicCrmPricing } from "./publicCrmPricing";
import { redirectToCrmDashboard } from "./publicNavigation";
import { recordAnalyticsEvent } from "./analytics";
import { salesMailto } from "../config/public";

const integrations = [
  {
    id: "instagram",
    name: "Instagram",
    solutionName: "Instagram CRM",
    solutionDescription: "Manage Instagram enquiries and comments as trackable leads with clear team follow-ups.",
    solutionCta: "Explore Instagram CRM",
    href: "/instagram-crm/",
  },
  {
    id: "facebook",
    name: "Facebook",
    solutionName: "Facebook Lead CRM",
    solutionDescription: "Manage Facebook Page comments and Messenger enquiries with lead ownership and follow-ups.",
    solutionCta: "Explore Facebook CRM",
    href: "/facebook-lead-crm/",
  },
  {
    id: "whatsapp",
    name: "WhatsApp",
    solutionName: "WhatsApp CRM",
    solutionDescription: "Manage WhatsApp leads and follow-ups, with eligible catalogue auto replies in a limited opt-in pilot.",
    solutionCta: "Explore WhatsApp CRM",
    href: "/whatsapp-crm/",
  },
] as const;

export function MarketingHome() {
  const hasSession = Boolean(getStoredToken());
  const [pricing, setPricing] = useState<PublicCrmPricing | null>(null);
  const [pricingFailed, setPricingFailed] = useState(false);

  useEffect(() => {
    document.title = leadCatBrand.marketing.title;
    if (hasSession) redirectToCrmDashboard();
  }, [hasSession]);

  useEffect(() => {
    if (hasSession) return;
    let active = true;
    fetchPublicCrmPricing()
      .then((result) => {
        if (active) setPricing(result);
      })
      .catch(() => {
        if (active) setPricingFailed(true);
      });
    return () => { active = false; };
  }, [hasSession]);

  if (hasSession) return null;

  return (
    <main className="pm-site pm-home">
      <header className="pm-header">
        <nav className="pm-nav" aria-label="Public navigation">
          <a className="pm-brand" href="/" aria-label="LeadCat CRM homepage">
            <BrandLogo product="crm" variant="lockup" size="small" className="pm-brand-logo" />
          </a>
          <div className="pm-nav-actions">
            <details className="pm-mobile-menu">
              <summary>Menu</summary>
              <nav aria-label="Mobile navigation" onClick={(event) => {
                if ((event.target as HTMLElement).closest("a")) event.currentTarget.closest("details")?.removeAttribute("open");
              }}>
                <a href="#features">Features</a><a href="#solutions">Solutions</a><a href="#trust">Why LeadCat</a><a href="#pricing" onClick={() => recordAnalyticsEvent("pricing_click", { source: "homepage_navigation", destination: "pricing_section" })}>Pricing</a><a href="/react/login">Sign in</a>
              </nav>
            </details>
            <a className="pm-nav-link" href="#features">Features</a>
            <a className="pm-nav-link pm-nav-product" href="#solutions">Solutions</a>
            <a className="pm-nav-link pm-nav-trust" href="#trust">Why LeadCat</a>
            <a
              className="pm-nav-link pm-nav-pricing"
              href="#pricing"
              onClick={() => recordAnalyticsEvent("pricing_click", {
                source: "homepage_navigation",
                destination: "pricing_section",
              })}
            >
              Pricing
            </a>
            <a className="pm-nav-link" href="/react/login">Sign in</a>
            <a className="pm-button pm-button-primary pm-button-compact" href="/react/signup">
              Get Started <span aria-hidden="true">→</span>
            </a>
          </div>
        </nav>
      </header>

      <section className="pm-hero" aria-labelledby="pm-hero-title">
        <div className="pm-hero-copy">
          <span className="pm-eyebrow"><i aria-hidden="true" /> Social enquiries. Real opportunities.</span>
          <h1 id="pm-hero-title">Turn social enquiries into <span>your next sale.</span></h1>
          <p className="pm-hero-intro">Capture enquiries from Instagram, Facebook and WhatsApp, assign them to your team, and keep every follow-up moving in one simple CRM.</p>
          <div className="pm-hero-actions">
            <a className="pm-button pm-button-primary" href="/react/signup">
              Get Started <span aria-hidden="true">→</span>
            </a>
            <a
              className="pm-button pm-button-secondary"
              href="https://wa.me/919176189950?text=Hi%2C%20I%20would%20like%20to%20request%20a%20demo%20of%20LeadCat%20CRM."
              target="_blank"
              rel="noreferrer"
              onClick={() => recordAnalyticsEvent("demo_contact_click", { channel: "homepage", destination: "whatsapp" })}
            >
              Book a Demo
            </a>
          </div>
          <div className="pm-assurance" aria-label="Workspace activation information">
            <span><i aria-hidden="true">✓</i> Private business workspace</span>
            <span><i aria-hidden="true">✓</i> Simple, transparent plans</span>
          </div>
          <div className="pm-hero-proof">
            <span className="pm-meta-approval"><i aria-hidden="true">✓</i>Meta app permissions approved</span>
            <a className="pm-watch-link" href="#demo">Watch Demo <span aria-hidden="true">↗</span></a>
          </div>
        </div>
        <figure className="pm-cat-stage">
          <LeadCatHeroArtwork priority alt="LeadCat cat mascot connecting Instagram, Facebook and WhatsApp conversations" sizes="(max-width: 860px) calc(100vw - 36px), 620px" />
          <figcaption>Meet LeadCat. A clearer path from conversation to conversion.</figcaption>
        </figure>
      </section>

      <div className="pm-channel-strip" aria-label="Supported customer channels">
        <span>One sales workflow. The channels you know.</span>
        <div>{integrations.map((integration) => <span className="pm-channel" key={integration.id}><LeadSourceIcon source={integration.name} size="small" />{integration.name}</span>)}</div>
      </div>

      <section className="pm-workflow" id="how-it-works" aria-labelledby="pm-workflow-title">
        <header className="pm-section-heading"><span>A little structure. A lot more clarity.</span><h2 id="pm-workflow-title">Less chasing messages.<br />More moving things forward.</h2><p>Give every enquiry an owner, a conversation history and a next step.</p></header>
        <ol className="pm-workflow-grid">
          <li><span>01 / Capture</span><h3>Bring enquiries together.</h3><p>Turn supported social messages and comments into leads, with the original channel context.</p></li>
          <li><span>02 / Assign</span><h3>Make ownership clear.</h3><p>Give each lead a team member who knows what needs to happen next.</p></li>
          <li><span>03 / Follow up</span><h3>Keep the promise to call back.</h3><p>See today’s follow-ups, keep notes and pick up where the conversation left off.</p></li>
          <li><span>04 / Convert</span><h3>Know what’s progressing.</h3><p>Track the sales pipeline and outcomes so you can see where your team needs support.</p></li>
        </ol>
        <figure className="pm-product-capture">
          <img src={`${import.meta.env.BASE_URL}assets/leadcat-crm-dashboard-demo.png`} width="1280" height="720" loading="lazy" decoding="async" alt="LeadCat CRM dashboard showing follow-up priorities, a business workspace and lead categories" />
          <figcaption><span>Actual LeadCat dashboard with demo data. Counts shown are examples, not customer results.</span><a href={`${import.meta.env.BASE_URL}assets/leadcat-crm-dashboard-demo.png`} target="_blank" rel="noreferrer">View full screenshot <span aria-hidden="true">↗</span></a></figcaption>
        </figure>
      </section>

      <MarketingFeatures />

      <MarketingDemoSection />

      <MarketingStorefrontStory />

      <section className="pm-solutions" id="solutions" aria-labelledby="pm-solutions-title">
        <header className="pm-section-heading">
          <span>Solutions</span>
          <h2 id="pm-solutions-title">Bring social enquiries into a sales process your team can follow</h2>
          <p>See how LeadCat gives customer interest from each channel clear ownership, follow-up and a visible next step.</p>
        </header>
        <div className="pm-solution-grid">
          {integrations.map((integration) => (
            <a className="pm-solution-card" href={integration.href} key={integration.id}>
              <LeadSourceIcon source={integration.name} decorative={false} imageAlt={integration.name} />
              <span>
                <span className="pm-solution-channel">{integration.name}<small>Available</small></span>
                <h3>{integration.solutionName}</h3>
                <p>{integration.solutionDescription}</p>
                <strong className="pm-solution-cta">{integration.solutionCta} <span aria-hidden="true">→</span></strong>
              </span>
            </a>
          ))}
        </div>
        <p className="pm-integration-note">Availability requires an eligible LeadCat workspace and a connected provider account. Platform names and symbols identify compatibility only and do not imply sponsorship or endorsement. WhatsApp catalogue auto replies are a limited, opt-in pilot.</p>
      </section>

      <section className="pm-value-journey" aria-labelledby="pm-journey-title">
        <header className="pm-journey-heading">
          <span>The LeadCat promise</span>
          <h2 id="pm-journey-title">Lead. Connect. Convert.</h2>
        </header>
        <div className="pm-value-strip">
          <article className="pm-value-card"><span>Lead</span><div><strong>Every enquiry. One clear pipeline.</strong><small>Instagram, Facebook, WhatsApp and manual leads land in one tenant-private workspace.</small></div></article>
          <article className="pm-value-card"><span>Connect</span><div><strong>Know exactly what needs attention.</strong><small>Assign owners, see new leads and find overdue follow-ups without searching.</small></div></article>
          <article className="pm-value-card"><span>Convert</span><div><strong>Make it easier to buy.</strong><small>Follow up with discipline, then give ready customers a direct route to the relevant buying destination.</small></div></article>
        </div>
        <p className="pm-commerce-signal"><strong>From enquiry to sale.</strong> Capture the enquiry, manage the follow-up and, with LeadCat Commerce enabled, move a ready customer toward your storefront. <a href="/social-commerce/">Explore social commerce CRM →</a></p>
      </section>

      <MarketingPricingSection
        pricing={pricing}
        loading={!pricing && !pricingFailed}
        failed={pricingFailed}
      />

      <section className="pm-trust" id="trust" aria-labelledby="pm-trust-title">
        <header className="pm-section-heading"><span>Confidence starts with clarity</span><h2 id="pm-trust-title">Your business.<br />Your relationships. Your control.</h2><p>Built by Staminal Technologies Private Limited, with clear account permissions and a team you can contact.</p></header>
        <div className="pm-trust-grid">
          <article><span aria-hidden="true">01</span><h3>A workspace for your business</h3><p>Your business works in its own workspace. Owner and staff roles help control access to your team’s work.</p><a href="/privacy.html">Read our privacy policy →</a></article>
          <article><span aria-hidden="true">02</span><h3>You choose the connections</h3><p>Connect eligible business accounts. Automatic replies are opt-in and use the messages you configure.</p><a href="/data-deletion.html">Understand data deletion →</a></article>
          <article><span aria-hidden="true">03</span><h3>A conversation before a commitment</h3><p>Ask us about your channels, setup and plan. See the workflow with our team before deciding what fits.</p><a href={salesMailto("LeadCat CRM setup and support")}>Contact the LeadCat team →</a></article>
        </div>
      </section>

      <section className="pm-questions" aria-labelledby="pm-questions-title">
        <header className="pm-section-heading"><span>Before you get started</span><h2 id="pm-questions-title">A few things you might be wondering.</h2></header>
        <div className="pm-question-list">
          <details><summary>What happens after I sign up?</summary><p>Create your account and business workspace, review your plan and activation terms, then connect eligible business accounts. If you need help with setup, contact our team.</p></details>
          <details><summary>Will every message become a lead?</summary><p>Capture depends on the connected channel, permissions and supported event types. We can walk through your account’s setup in a demo so you know what to expect.</p></details>
          <details><summary>Does LeadCat automatically reply to customers?</summary><p>Automatic replies are optional and must be configured. Instagram and Facebook support eligible comment and private replies. WhatsApp catalogue auto replies are currently a limited, opt-in pilot.</p></details>
          <details><summary>Can my team share the follow-up work?</summary><p>Yes. Assign leads to staff, keep notes and track follow-ups in the business workspace. Included users and additional-user prices are shown in the CRM plan above.</p></details>
        </div>
      </section>

      <section className="pm-resources" aria-labelledby="pm-resources-title">
        <header className="pm-section-heading"><span>Practical resources</span><h2 id="pm-resources-title">Get more from every conversation.</h2></header>
        <div className="pm-resource-grid">
          <article><h3>Instagram</h3><a href="/blog/how-to-manage-instagram-comments-for-business/">Manage Instagram comments and sales enquiries →</a><a href="/blog/best-tools-to-auto-reply-instagram-comments/">Compare tools to auto reply to Instagram comments →</a><a href="/instagram-comment-auto-reply/">Automatic reply to Instagram comments →</a><a href="/instagram-crm-india/">Instagram CRM for Indian businesses →</a></article>
          <article><h3>Facebook</h3><a href="/blog/how-to-manage-facebook-page-comments-and-messenger-enquiries/">Manage Facebook Page comments and Messenger enquiries →</a><a href="/blog/best-tools-to-auto-reply-facebook-comments/">Compare tools to auto reply to Facebook comments →</a></article>
          <article><h3>WhatsApp</h3><a href="/blog/how-to-manage-whatsapp-leads-and-auto-replies/">Manage WhatsApp leads and eligible auto replies →</a><a href="/blog/best-tools-to-auto-reply-whatsapp-messages/">Compare automatic reply tools for WhatsApp messages →</a></article>
        </div>
      </section>

      <section className="pm-final-cta" aria-labelledby="pm-final-title">
        <span>Simplicity is our audacity.</span><h2 id="pm-final-title">Your next enquiry<br />deserves a next step.</h2><p>Bring your conversations, your team and your follow-ups together with LeadCat.</p>
        <div className="pm-hero-actions"><a className="pm-button pm-button-primary" href="/react/signup">Get Started <span aria-hidden="true">→</span></a><a className="pm-button pm-button-secondary" href={salesMailto("LeadCat CRM demo")}>Talk to the LeadCat team</a></div>
      </section>

      <footer className="pm-footer">
        <div className="pm-footer-brand">
          <a href="/" aria-label="LeadCat CRM homepage">
            <BrandLogo product="crm" variant="lockup" size="small" className="pm-footer-logo" />
          </a>
          <p>LeadCat CRM is a product from Staminal Technologies.</p>
        </div>
        <div className="pm-footer-meta">
          <p>{leadCatBrand.legalLine}</p>
          <div>
            <span>© {new Date().getFullYear()} Staminal Technologies Private Limited</span>
            <nav aria-label="Legal links">
              <a href="/instagram-crm/">Instagram CRM</a>
              <a href="/instagram-comment-auto-reply/">Automatic Reply to Instagram Comments</a>
              <a href="/instagram-crm-india/">Instagram CRM India</a>
              <a href="/facebook-lead-crm/">Facebook CRM</a>
              <a href="/whatsapp-crm/">WhatsApp CRM</a>
              <a href="/social-commerce/">Social Commerce CRM</a>
              <a href="/privacy.html">Privacy</a>
              <a href="/terms.html">Terms</a>
              <a href="/dpa.html">DPA</a>
              <a href="/data-deletion.html">Data deletion</a>
            </nav>
          </div>
        </div>
      </footer>
    </main>
  );
}
